import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { createReading, getReadingById, updateReading } from "../../api/readingApi";
import {
  Box,
  Typography,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Avatar,
  Paper,
  Chip,
  Tooltip,
  LinearProgress,
  Fade,
  Zoom,
  Alert,
  Stack,
} from "@mui/material";
import {
  MenuBook as MenuBookIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";
import { useToast } from "../../contexts/ToastContext";
import Editor from "../../components/common/Editor";
import ModernButton from "../../components/common/ModernButton";
import ModernTextField from "../../components/common/ModernTextField";
import {
  parseInterval,
  parseIntervalToCount,
  validateIntervalSequence,
  calculateTotalQuestionCount,
  validateTotalQuestionCount,
} from "../../utils/intervalUtils";

const ReadingForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [reading, setReading] = useState({
    label: "",
    isPractice: false,
    passage1: {
      passage: 1,
      totalQuestionCount: 0,
      totalQuestionInterval: "",
      instructionLabel: "",
      passageArticle: "",
    },
    passage2: {
      passage: 2,
      totalQuestionCount: 0,
      totalQuestionInterval: "",
      instructionLabel: "",
      passageArticle: "",
    },
    passage3: {
      passage: 3,
      totalQuestionCount: 0,
      totalQuestionInterval: "",
      instructionLabel: "",
      passageArticle: "",
    },
  });

  const [totalQuestionCountError, setTotalQuestionCountError] = useState("");
  const [intervalError, setIntervalError] = useState("");
  const [errors, setErrors] = useState({
    passage1: {},
    passage2: {},
    passage3: {},
  });
  const editorRefs = {
    passage1: useRef(),
    passage2: useRef(),
    passage3: useRef(),
  };

  const [activeStep, setActiveStep] = useState(0);
  const passageKeys = ["passage1", "passage2", "passage3"];
  const [formProgress, setFormProgress] = useState(0);
  const [completedPassages, setCompletedPassages] = useState({});

  // Calculate form completion percentage
  useEffect(() => {
    const totalFields = 3 + passageKeys.length * 3; // label + (instructionLabel, interval, article) * 3
    let filledFields = reading.label ? 1 : 0;

    passageKeys.forEach(key => {
      if (reading[key].instructionLabel) filledFields++;
      if (reading[key].totalQuestionInterval) filledFields++;
      if (reading[key].passageArticle && reading[key].passageArticle !== '<p><br></p>') filledFields++;
    });

    const progress = (filledFields / totalFields) * 100;
    setFormProgress(progress);

    // Update completed passages
    const completed = {};
    passageKeys.forEach(key => {
      const passage = reading[key];
      const html = editorRefs[key].current?.getHTML() || passage.passageArticle || '';
      completed[key] = !!(
        passage.instructionLabel &&
        passage.totalQuestionInterval &&
        html &&
        html !== '<p><br></p>' &&
        !errors[key]?.instructionLabel &&
        !errors[key]?.totalQuestionInterval &&
        !errors[key]?.passageArticle
      );
    });
    setCompletedPassages(completed);
  }, [reading, errors]);

  const saveCurrentPassageArticle = () => {
    const key = passageKeys[activeStep];
    const html = editorRefs[key].current?.getHTML() || reading[key].passageArticle || "";
    setReading((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        passageArticle: html,
      },
    }));
  };

  const validatePassage = (key) => {
    const errs = {};
    const data = reading[key];
    if (!data.instructionLabel.trim()) {
      errs.instructionLabel = "Instruction label is required";
    }
    if (!data.totalQuestionInterval.trim()) {
      errs.totalQuestionInterval = "Question interval is required";
    }
    const html =
      (editorRefs[key].current?.getHTML() || data.passageArticle || "").trim();
    if (!html || html === "<p><br></p>") {
      errs.passageArticle = "Passage article is required";
    }
    return errs;
  };


  useEffect(() => {
    if (id) {
      const fetchReading = async () => {
        try {
          const { data } = await getReadingById(id);
          setReading({
            ...data,
            isPractice: data.isPractice || false,
            passage1: { passage: 1, ...data.passage1 },
            passage2: { passage: 2, ...data.passage2 },
            passage3: { passage: 3, ...data.passage3 },
          });
          Object.keys(editorRefs).forEach((key) => {
            const passageArticle = data[key]?.passageArticle || "";
            if (editorRefs[key].current) {
              editorRefs[key].current.setHTML(passageArticle);
            }
          });
        } catch (error) {
          console.error("Failed to fetch reading:", error);
        }
      };
      fetchReading();
    }
  }, [id]);

  // Live validation for total question count across passages
  useEffect(() => {
    const totalCount = calculateTotalQuestionCount(reading, passageKeys);
    setTotalQuestionCountError(validateTotalQuestionCount(totalCount));
  }, [
    reading.passage1.totalQuestionCount,
    reading.passage2.totalQuestionCount,
    reading.passage3.totalQuestionCount,
  ]);

  // Validation for non-overlapping intervals across passages.
  useEffect(() => {
    const intervals = passageKeys.map(key => reading[key].totalQuestionInterval);
    setIntervalError(validateIntervalSequence(intervals, 'Passage'));
  }, [
    reading.passage1.totalQuestionInterval,
    reading.passage2.totalQuestionInterval,
    reading.passage3.totalQuestionInterval,
  ]);

  const handleChange = (e, passageKey) => {
    const { name, value, type, checked } = e.target;
    if (passageKey) {
      if (name === "totalQuestionInterval") {
        // Auto-calculate total question count from the interval string.
        const calculatedCount = parseIntervalToCount(value);
        setReading((prev) => ({
          ...prev,
          [passageKey]: {
            ...prev[passageKey],
            totalQuestionInterval: value,
            totalQuestionCount: calculatedCount,
          },
        }));
        setErrors((prev) => ({
          ...prev,
          [passageKey]: { ...prev[passageKey], totalQuestionInterval: "" },
        }));
      } else {
        setReading((prev) => ({
          ...prev,
          [passageKey]: {
            ...prev[passageKey],
            [name]: value,
          },
        }));
        setErrors((prev) => ({
          ...prev,
          [passageKey]: { ...prev[passageKey], [name]: "" },
        }));
      }
    } else {
      setReading((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    saveCurrentPassageArticle();
    const collectedErrors = {};
    passageKeys.forEach((k) => {
      collectedErrors[k] = validatePassage(k);
    });
    const hasErrors =
      totalQuestionCountError ||
      intervalError ||
      Object.values(collectedErrors).some((er) => Object.keys(er).length > 0);
    if (hasErrors) {
      setErrors(collectedErrors);
      showToast("Please fix validation errors before saving.", "error");
      return;
    }

    const updatedReading = { ...reading };
    Object.keys(editorRefs).forEach((key) => {
      let passageArticle = editorRefs[key].current?.getHTML().trim();
      if (!passageArticle) {
        passageArticle = reading[key].passageArticle || "";
      }
      updatedReading[key].passageArticle =
        passageArticle === "<p><br></p>" ? "" : passageArticle;
    });

    try {
      if (id) {
        await updateReading(id, updatedReading);
      } else {
        await createReading(updatedReading);
      }
      showToast("Reading saved successfully!", "success");
      navigate("/readings");
    } catch (error) {
      console.error("Failed to save reading:", error);
      showToast("Failed to save reading", "error");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
      e.preventDefault();
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Fade in timeout={500}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Zoom in timeout={600}>
              <Avatar
                sx={{
                  backgroundColor: 'primary.main',
                  width: 56,
                  height: 56,
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                <MenuBookIcon fontSize="large" />
              </Avatar>
            </Zoom>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                {id ? "Edit Reading" : "Create Reading"}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                {id ? 'Update reading content and passages' : 'Set up a new reading with multiple passages and questions'}
              </Typography>
            </Box>
            <Chip
              label={`${Math.round(formProgress)}% Complete`}
              color={formProgress === 100 ? 'success' : 'primary'}
              icon={formProgress === 100 ? <CheckCircleIcon /> : undefined}
              sx={{ fontWeight: 600, px: 1 }}
            />
          </Box>

          {/* Progress Bar */}
          <Paper elevation={0} sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                Form Completion
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {Math.round(formProgress)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={formProgress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: formProgress === 100
                    ? 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)'
                    : 'linear-gradient(90deg, #2196f3 0%, #42a5f5 100%)',
                }
              }}
            />
          </Paper>
        </Box>
      </Fade>

      <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
        <Grid container spacing={3}>
          {/* Basic Information Card */}
          <Grid item xs={12}>
            <Fade in timeout={700}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ backgroundColor: 'info.main', width: 40, height: 40, mr: 2 }}>
                      <InfoIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', flexGrow: 1 }}>
                      Basic Information
                    </Typography>
                    <Tooltip title="Enter the reading title and mark if it's a practice test">
                      <HelpOutlineIcon sx={{ color: 'action.active', cursor: 'pointer' }} fontSize="small" />
                    </Tooltip>
                  </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <ModernTextField
                      label="Reading Title"
                      name="label"
                      value={reading.label}
                      onChange={handleChange}
                      fullWidth
                      required
                      placeholder="Enter a descriptive title for this reading test"
                      InputProps={{
                        endAdornment: reading.label && (
                          <CheckCircleIcon color="success" />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            name="isPractice" 
                            checked={reading.isPractice}
                            sx={{
                              '&.Mui-checked': {
                                color: 'primary.main',
                              },
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              Practice Test
                            </Typography>
                            <Chip 
                              label={reading.isPractice ? 'Yes' : 'No'} 
                              color={reading.isPractice ? 'info' : 'default'}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        }
                        onChange={(e) => handleChange(e)}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            </Fade>
          </Grid>

          {/* Progress Stepper */}
          <Grid item xs={12}>
            <Fade in timeout={800}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ backgroundColor: 'success.main', width: 40, height: 40, mr: 2 }}>
                      <CheckCircleIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Passage Progress
                    </Typography>
                  </Box>

                  <Stepper
                    activeStep={activeStep}
                    sx={{
                      '& .MuiStepLabel-root': {
                        '& .MuiStepLabel-label': {
                          fontWeight: 600,
                          fontSize: '1rem',
                        },
                      },
                    }}
                  >
                    {passageKeys.map((key, idx) => (
                      <Step key={key} completed={completedPassages[key]}>
                        <StepLabel
                          StepIconProps={{
                            icon: completedPassages[key] ? <CheckCircleIcon /> : idx + 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>Passage {idx + 1}</Typography>
                            {reading[key]?.totalQuestionCount > 0 && (
                              <Chip
                                label={`${reading[key].totalQuestionCount} questions`}
                                size="small"
                                color={completedPassages[key] ? "success" : "primary"}
                                variant="outlined"
                              />
                            )}
                            {completedPassages[key] && (
                              <Chip
                                icon={<CheckCircleIcon />}
                                label="Complete"
                                size="small"
                                color="success"
                              />
                            )}
                          </Box>
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        </Grid>

        {/* Current Passage Content */}
        {(() => {
          const passageKey = passageKeys[activeStep];
          return (
            <Zoom in timeout={900} key={passageKey}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ backgroundColor: 'warning.main', width: 40, height: 40, mr: 2 }}>
                      <MenuBookIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', flexGrow: 1 }}>
                      Passage {activeStep + 1} Content
                    </Typography>
                    {completedPassages[passageKey] && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Complete"
                        color="success"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    <Tooltip title="Enter instruction label, question interval, and passage article">
                      <HelpOutlineIcon sx={{ ml: 1, color: 'action.active', cursor: 'pointer' }} fontSize="small" />
                    </Tooltip>
                  </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      label="Instruction Label"
                      name="instructionLabel"
                      value={reading[passageKey]?.instructionLabel || ""}
                      onChange={(e) => handleChange(e, passageKey)}
                      fullWidth
                      required
                      placeholder="e.g., Questions 1-13"
                      error={Boolean(errors[passageKey]?.instructionLabel)}
                      helperText={errors[passageKey]?.instructionLabel}
                      InputProps={{
                        endAdornment: reading[passageKey]?.instructionLabel && !errors[passageKey]?.instructionLabel && (
                          <CheckCircleIcon color="success" />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                        Passage Number
                      </Typography>
                      <Chip 
                        label={`Passage ${activeStep + 1}`}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                      Passage Article
                    </Typography>
                    <Paper sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 2 }}>
                      <Editor
                        ref={editorRefs[passageKey]}
                        defaultValue={reading[passageKey].passageArticle || ""}
                      />
                    </Paper>
                    {errors[passageKey]?.passageArticle && (
                      <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {errors[passageKey].passageArticle}
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      label="Question Interval (e.g., 1-13)"
                      name="totalQuestionInterval"
                      type="text"
                      value={reading[passageKey]?.totalQuestionInterval || ""}
                      onChange={(e) => handleChange(e, passageKey)}
                      fullWidth
                      required
                      placeholder="e.g., 1-13"
                      error={Boolean(errors[passageKey]?.totalQuestionInterval)}
                      helperText={errors[passageKey]?.totalQuestionInterval || "Format: start-end"}
                      InputProps={{
                        endAdornment: reading[passageKey]?.totalQuestionInterval && !errors[passageKey]?.totalQuestionInterval && (
                          <CheckCircleIcon color="success" />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                        Question Count (Auto-calculated)
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        {reading[passageKey]?.totalQuestionCount || 0} questions
                      </Typography>
                    </Box>
                  </Grid>

                  {completedPassages[passageKey] && (
                    <Grid item xs={12}>
                      <Fade in>
                        <Alert severity="success">
                          Passage {activeStep + 1} completed successfully!
                        </Alert>
                      </Fade>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
            </Zoom>
          );
        })()}
        {/* Error Messages */}
        {(totalQuestionCountError || intervalError) && (
          <Card sx={{ borderRadius: 3, mb: 3, border: '1px solid', borderColor: 'error.main' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ backgroundColor: 'error.main', width: 32, height: 32, mr: 2 }}>
                  <InfoIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
                  Validation Errors
                </Typography>
              </Box>
              {totalQuestionCountError && (
                <Typography color="error" sx={{ mb: 1 }}>
                  {totalQuestionCountError}
                </Typography>
              )}
              {intervalError && (
                <Typography color="error">
                  {intervalError}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            bottom: 20,
            zIndex: 10,
            p: 2.5,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}
        >
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <ModernButton
              type="button"
              disabled={activeStep === 0}
              variant="outlined"
              startIcon={<NavigateBeforeIcon />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                saveCurrentPassageArticle();
                setActiveStep((s) => s - 1);
              }}
            >
              Previous
            </ModernButton>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon color="primary" fontSize="small" />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {formProgress === 100 ? 'All passages completed!' : `Passage ${activeStep + 1} of ${passageKeys.length}`}
              </Typography>
            </Box>

            <Stack direction="row" spacing={2}>
              <ModernButton
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate('/readings');
                }}
                variant="outlined"
                startIcon={<ArrowBackIcon />}
              >
                Back to List
              </ModernButton>

              {activeStep < passageKeys.length - 1 ? (
                <ModernButton
                  type="button"
                  variant="contained"
                  endIcon={<NavigateNextIcon />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    saveCurrentPassageArticle();
                    const key = passageKeys[activeStep];
                    const errs = validatePassage(key);
                    if (Object.keys(errs).length > 0) {
                      setErrors((prev) => ({ ...prev, [key]: errs }));
                    } else {
                      setErrors((prev) => ({ ...prev, [key]: {} }));
                      setActiveStep((s) => s + 1);
                    }
                  }}
                  sx={{
                    minWidth: 140,
                    background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    },
                  }}
                >
                  Next Passage
                </ModernButton>
              ) : (
                <ModernButton
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={!!totalQuestionCountError || !!intervalError}
                  sx={{
                    minWidth: 180,
                    background: formProgress === 100
                      ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
                      : 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                    '&:hover': {
                      background: formProgress === 100
                        ? 'linear-gradient(135deg, #66bb6a 0%, #81c784 100%)'
                        : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    },
                  }}
                >
                  Save Reading
                </ModernButton>
              )}
            </Stack>
          </Stack>
        </Paper>
      </form>
    </Box>
  );
};

export default ReadingForm;
